import { all, fork, put, takeEvery, call } from 'redux-saga/effects';
import { SagaIterator } from '@redux-saga/core';
import {
    getRituals as getRitualsApi,
    getRitualById as getRitualByIdApi,
    addRitual as addRitualApi,
    updateRitual as updateRitualApi,
    deleteRitual as deleteRitualApi,
    tagServiceToRituals as tagServiceToRitualsApi,
    tagProductToRituals as tagProductToRitualsApi,
    assignRitualToPoojari as assignRitualToPoojariApi,
    getRitualsByPoojari as getRitualsByPoojariApi,
    getPoojarisByRitual as getPoojarisByRitualApi,
    updatePoojariRitual as updatePoojariRitualApi,
    removeRitualFromPoojari as removeRitualFromPoojariApi,
} from 'helpers/api/rituals';
import { ritualApiResponseSuccess, ritualApiResponseError } from './actions';
import { RitualActionTypes } from './constants';

/**
 * Get all rituals
 */
function* getRituals({ payload }: any): SagaIterator {
    try {
        const response = yield call(getRitualsApi, payload || {});
        yield put(ritualApiResponseSuccess(RitualActionTypes.GET_RITUALS, response.data));
    } catch (error: any) {
        console.error('Error fetching rituals:', error);
        yield put(ritualApiResponseError(RitualActionTypes.GET_RITUALS, error));
    }
}

/**
 * Get ritual by ID
 */
function* getRitualById({ payload }: any): SagaIterator {
    try {
        const response = yield call(getRitualByIdApi, payload);
        const ritual = response.data;
        yield put(ritualApiResponseSuccess(RitualActionTypes.GET_RITUAL_BY_ID, ritual));
    } catch (error: any) {
        yield put(ritualApiResponseError(RitualActionTypes.GET_RITUAL_BY_ID, error));
    }
}

/**
 * Add new ritual
 */
function* addRitual({ payload }: any): SagaIterator {
    try {
        const response = yield call(addRitualApi, { ritualDetails: payload });
        const createdRitual = response.data;
        yield put(ritualApiResponseSuccess(RitualActionTypes.ADD_RITUAL, createdRitual));
    } catch (error: any) {
        console.error('Error creating ritual:', error);
        yield put(ritualApiResponseError(RitualActionTypes.ADD_RITUAL, error));
    }
}

/**
 * Update ritual
 */
function* updateRitual({ payload }: any): SagaIterator {
    try {
        const { ritualId, ritualData } = payload;
        const response = yield call(updateRitualApi, { ritualId, ritualDetails: ritualData });
        const updatedRitual = response.data;
        yield put(ritualApiResponseSuccess(RitualActionTypes.UPDATE_RITUAL, updatedRitual));
    } catch (error: any) {
        yield put(ritualApiResponseError(RitualActionTypes.UPDATE_RITUAL, error));
    }
}

/**
 * Delete ritual
 */
function* deleteRitual({ payload }: any): SagaIterator {
    try {
        yield call(deleteRitualApi, payload);
        yield put(ritualApiResponseSuccess(RitualActionTypes.DELETE_RITUAL, payload.ritualId));
    } catch (error: any) {
        yield put(ritualApiResponseError(RitualActionTypes.DELETE_RITUAL, error));
    }
}

/**
 * Tag service to rituals
 */
function* tagServiceToRituals({ payload }: any): SagaIterator {
    try {
        yield call(tagServiceToRitualsApi, payload);
        yield put(ritualApiResponseSuccess(RitualActionTypes.TAG_SERVICE_TO_RITUALS, payload));
    } catch (error: any) {
        yield put(ritualApiResponseError(RitualActionTypes.TAG_SERVICE_TO_RITUALS, error));
    }
}

/**
 * Tag product to rituals
 */
function* tagProductToRituals({ payload }: any): SagaIterator {
    try {
        yield call(tagProductToRitualsApi, payload);
        yield put(ritualApiResponseSuccess(RitualActionTypes.TAG_PRODUCT_TO_RITUALS, payload));
    } catch (error: any) {
        yield put(ritualApiResponseError(RitualActionTypes.TAG_PRODUCT_TO_RITUALS, error));
    }
}

/**
 * Assign ritual to poojari
 */
function* assignRitualToPoojari({ payload }: any): SagaIterator {
    try {
        const response = yield call(assignRitualToPoojariApi, payload);
        const assignment = response.data;
        yield put(ritualApiResponseSuccess(RitualActionTypes.ASSIGN_RITUAL_TO_POOJARI, assignment));
    } catch (error: any) {
        yield put(ritualApiResponseError(RitualActionTypes.ASSIGN_RITUAL_TO_POOJARI, error));
    }
}

/**
 * Get rituals by poojari
 */
function* getRitualsByPoojari({ payload }: any): SagaIterator {
    try {
        const response = yield call(getRitualsByPoojariApi, payload);
        const rituals = response.data?.results || response.data;
        yield put(ritualApiResponseSuccess(RitualActionTypes.GET_RITUALS_BY_POOJARI, rituals));
    } catch (error: any) {
        yield put(ritualApiResponseError(RitualActionTypes.GET_RITUALS_BY_POOJARI, error));
    }
}

/**
 * Get poojaris by ritual
 */
function* getPoojarisByRitual({ payload }: any): SagaIterator {
    try {
        const response = yield call(getPoojarisByRitualApi, payload);
        const poojaris = response.data?.results || response.data;
        yield put(ritualApiResponseSuccess(RitualActionTypes.GET_POOJARIS_BY_RITUAL, poojaris));
    } catch (error: any) {
        yield put(ritualApiResponseError(RitualActionTypes.GET_POOJARIS_BY_RITUAL, error));
    }
}

/**
 * Update poojari ritual
 */
function* updatePoojariRitual({ payload }: any): SagaIterator {
    try {
        const response = yield call(updatePoojariRitualApi, payload);
        const updated = response.data;
        yield put(ritualApiResponseSuccess(RitualActionTypes.UPDATE_POOJARI_RITUAL, updated));
    } catch (error: any) {
        yield put(ritualApiResponseError(RitualActionTypes.UPDATE_POOJARI_RITUAL, error));
    }
}

/**
 * Remove ritual from poojari
 */
function* removeRitualFromPoojari({ payload }: any): SagaIterator {
    try {
        yield call(removeRitualFromPoojariApi, payload);
        yield put(ritualApiResponseSuccess(RitualActionTypes.REMOVE_RITUAL_FROM_POOJARI, payload));
    } catch (error: any) {
        yield put(ritualApiResponseError(RitualActionTypes.REMOVE_RITUAL_FROM_POOJARI, error));
    }
}

/**
 * Bulk import rituals (one-by-one via addRitual API)
 */
function* bulkImportRituals({ payload: { rituals } }: any): SagaIterator {
    try {
        const total = rituals.length;
        const errors: string[] = [];

        for (let i = 0; i < total; i++) {
            try {
                yield call(addRitualApi, { ritualDetails: rituals[i] });
                yield put({
                    type: RitualActionTypes.API_RESPONSE_SUCCESS,
                    payload: {
                        actionType: '@@rituals/BULK_IMPORT_PROGRESS',
                        progress: Math.round(((i + 1) / total) * 100),
                    },
                });
            } catch (err: any) {
                errors.push(`Row ${i + 1}: ${err?.response?.data?.message || err.message || 'Failed'}`);
            }
        }

        if (errors.length === total) {
            yield put(ritualApiResponseError(RitualActionTypes.BULK_IMPORT_RITUALS, errors.join('; ')));
        } else {
            yield put(ritualApiResponseSuccess(RitualActionTypes.BULK_IMPORT_RITUALS, {}));
        }
    } catch (error: any) {
        yield put(ritualApiResponseError(RitualActionTypes.BULK_IMPORT_RITUALS, error?.message || 'Import failed'));
    }
}

/**
 * Watchers
 */
export function* watchGetRituals() {
    yield takeEvery(RitualActionTypes.GET_RITUALS, getRituals);
}

export function* watchGetRitualById() {
    yield takeEvery(RitualActionTypes.GET_RITUAL_BY_ID, getRitualById);
}

export function* watchAddRitual() {
    yield takeEvery(RitualActionTypes.ADD_RITUAL, addRitual);
}

export function* watchUpdateRitual() {
    yield takeEvery(RitualActionTypes.UPDATE_RITUAL, updateRitual);
}

export function* watchDeleteRitual() {
    yield takeEvery(RitualActionTypes.DELETE_RITUAL, deleteRitual);
}

export function* watchTagServiceToRituals() {
    yield takeEvery(RitualActionTypes.TAG_SERVICE_TO_RITUALS, tagServiceToRituals);
}

export function* watchTagProductToRituals() {
    yield takeEvery(RitualActionTypes.TAG_PRODUCT_TO_RITUALS, tagProductToRituals);
}

export function* watchAssignRitualToPoojari() {
    yield takeEvery(RitualActionTypes.ASSIGN_RITUAL_TO_POOJARI, assignRitualToPoojari);
}

export function* watchGetRitualsByPoojari() {
    yield takeEvery(RitualActionTypes.GET_RITUALS_BY_POOJARI, getRitualsByPoojari);
}

export function* watchGetPoojarisByRitual() {
    yield takeEvery(RitualActionTypes.GET_POOJARIS_BY_RITUAL, getPoojarisByRitual);
}

export function* watchUpdatePoojariRitual() {
    yield takeEvery(RitualActionTypes.UPDATE_POOJARI_RITUAL, updatePoojariRitual);
}

export function* watchRemoveRitualFromPoojari() {
    yield takeEvery(RitualActionTypes.REMOVE_RITUAL_FROM_POOJARI, removeRitualFromPoojari);
}

export function* watchBulkImportRituals() {
    yield takeEvery(RitualActionTypes.BULK_IMPORT_RITUALS, bulkImportRituals);
}

/**
 * Root ritual saga
 */
function* ritualSaga() {
    yield all([
        fork(watchGetRituals),
        fork(watchGetRitualById),
        fork(watchAddRitual),
        fork(watchUpdateRitual),
        fork(watchDeleteRitual),
        fork(watchTagServiceToRituals),
        fork(watchTagProductToRituals),
        fork(watchAssignRitualToPoojari),
        fork(watchGetRitualsByPoojari),
        fork(watchGetPoojarisByRitual),
        fork(watchUpdatePoojariRitual),
        fork(watchRemoveRitualFromPoojari),
        fork(watchBulkImportRituals),
    ]);
}

export default ritualSaga;
